import {
  Box,
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Add } from '@mui/icons-material';
import useTable from 'src/hooks/useTable';
import { TableHeadCustom } from 'src/components/table';
import { useEpisodeOfCares } from 'src/careflow/ph-core/fhir-client/fhir-react';
import {
  PatientWrapper,
  WrappedPatient,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { TABLE_HEAD_EPISODE_OF_CARE } from 'src/careflow/common/table-head';
import { v4 as uuidv4 } from 'uuid';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { translateTableHead } from 'src/careflow/helpers/common';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { Row } from './Row';
import {
  EpisodeOfCareWrapper,
  WrappedEpisodeOfCare,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/EpisodeOfCare';
import { EpisodeOfCareForm } from './EpisodeOfCareForm';
import EpisodeOfCareDetailDrawer from './EpisodeOfCareDetailDrawer';
import { PractitionerWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import { PractitionerRoleWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole';
import {
  ConditionWrapper,
  WrappedCondition,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import {
  ServiceRequestWrapper,
  WrappedServiceRequest,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { RelatedPersonWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/RelatedPerson';
import {
  OrganizationWrapper,
  WrappedOrganization,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';

type Props = {
  patient: WrappedPatient | null;
};

interface ConditionWithRecorder extends WrappedCondition {
  recorderResource?: {
    id?: string;
    display?: string;
  };
  rank: string;
}

export interface EpisodeOfCareWithCareManager extends WrappedEpisodeOfCare {
  includedResources: {
    careManager?: {
      id?: string;
      display?: string;
      role?: string;
    };
    subject?: WrappedPatient | null;
    serviceRequests?: WrappedServiceRequest[] | null;
    conditions?: ConditionWithRecorder[] | null;
    organization?: WrappedOrganization | null;
  };
}

export function EpisodeOfCareList({ patient }: Props) {
  const [openModal, setOpenModal] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedEpisodeOfCare, setSelectedEpisodeOfCare] = useState<WrappedEpisodeOfCare | null>(
    null
  );
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({});

  const { i18n } = useLocales();

  const [episodesOfCare, { refresh: refetchEpisodesOfCare }] = useEpisodeOfCares({
    filter: {
      patient: patient?.id,
      _include: 'EpisodeOfCare:care-manager',
      '_include:iterate': ['PractitionerRole:practitioner'],
    },
    map: EpisodeOfCareWrapper,
    autofetch: !!patient?.id,
  });

  const mapEpisodeOfCare = useMemo(
    () => mapEpisodeOfCareResources(episodesOfCare),
    [episodesOfCare]
  );

  const paginatedEpisodesOfCare =
    mapEpisodeOfCare?.slice?.(page * rowsPerPage, page * rowsPerPage + rowsPerPage) ?? [];

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <TableContainer>
            <Stack sx={{ m: 1 }}>
              <Grid container display={'flex'} alignItems={'center'} paddingRight={2}>
                {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.EPISODE_OF_CARE.ADD] }) && (
                  <Grid item xs={2} display={'flex'} justifyContent={'flex-end'}>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ height: '36px' }}
                      startIcon={<Add />}
                      onClick={() => setOpenModal(true)}
                      style={{ position: 'absolute', top: '25px', right: '25px' }}
                    >
                      {i18n('patients.details.episodeOfCare.titleAddEpisodeOfCare', 'crs')}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Stack>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHeadCustom
                headLabel={translateTableHead(TABLE_HEAD_EPISODE_OF_CARE, i18n, 'crs')}
              />
              <TableBody>
                {paginatedEpisodesOfCare.length ? (
                  paginatedEpisodesOfCare.map((row: any) => (
                    <Row
                      key={uuidv4()}
                      row={row}
                      onData={(episodeOfCare) => {
                        setSelectedEpisodeOfCare(episodeOfCare);
                        setOpenModal(true);
                      }}
                      onDetailsClick={(episodeOfCare) => {
                        setSelectedEpisodeOfCare(episodeOfCare);
                        setIsDrawerOpen(true);
                      }}
                    />
                  ))
                ) : (
                  <TableCell colSpan={TABLE_HEAD_EPISODE_OF_CARE?.length}>
                    <Typography variant="body2" align="center">
                      No rows
                    </Typography>
                  </TableCell>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={episodesOfCare?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      </Box>
      <EpisodeOfCareDetailDrawer
        episodeOfCare={selectedEpisodeOfCare}
        open={isDrawerOpen}
        onCloseIconButtonClick={() => {
          setIsDrawerOpen(false);
          setSelectedEpisodeOfCare(null);
        }}
        onEdit={(episodeOfCare) => {
          setIsDrawerOpen(false);
          setSelectedEpisodeOfCare(episodeOfCare);
          setOpenModal(true);
        }}
        onSuccesfulEdit={() => {
          refetchEpisodesOfCare();
        }}
        isAllowedToEdit={checkAclValidation({ acls: [crsAcls.CRS.PATIENT.EPISODE_OF_CARE.EDIT] })}
        title="Episode Of Care Details"
      />
      <EpisodeOfCareForm
        patient={patient}
        episodeOfCare={selectedEpisodeOfCare}
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setSelectedEpisodeOfCare(null);
        }}
      />
    </>
  );
}

const getRecorderDisplayName = (resource: any): string => {
  if (!resource) return '';

  switch (resource.resourceType) {
    case 'Patient':
      return PatientWrapper(resource)?.getFullName?.() ?? '';

    case 'Practitioner':
      return PractitionerWrapper(resource)?.getFullName?.() ?? '';

    case 'RelatedPerson':
      return RelatedPersonWrapper(resource)?.getFullName?.() ?? '';

    default:
      return '';
  }
};

export const mapEpisodeOfCareResources = (resources: any[]): EpisodeOfCareWithCareManager[] => {
  try {
    const practitioner = new Map<string, any>();
    const practitionerRole = new Map<string, any>();
    const serviceRequests = new Map<string, any>();
    const diagnosis = new Map<string, any>();
    const recorderResources = new Map<string, any>();
    let relatedPerson: any = null;
    let patient: any = null;
    let organization: any = null;

    if (!resources?.length) return [];

    resources.forEach((resource) => {
      switch (resource.resourceType) {
        case 'Practitioner':
          practitioner.set(resource.id, resource);
          break;
        case 'PractitionerRole':
          practitionerRole.set(resource.id, resource);
          break;
        case 'ServiceRequest':
          serviceRequests.set(resource.id, resource);
          break;
        case 'Condition':
          diagnosis.set(resource.id, resource);
          break;
        case 'Patient':
          patient = resource;
          break;
        case 'RelatedPerson':
          relatedPerson = resource;
          break;
        case 'Organization':
          organization = resource;
      }
    });

    return resources
      .filter((resource) => resource.resourceType === 'EpisodeOfCare')
      .map((episode) => {
        const practitionerRoleId = episode?.careManager?.reference?.split('/')[1];
        const practitionerRoleR = practitionerRole.get(practitionerRoleId);
        const practitionerId = practitionerRoleR?.practitioner?.reference?.split('/')[1];
        const practitionerR = practitioner.get(practitionerId);
        const episodeServiceRequests =
          episode?.referralRequest?.flatMap((ref: any) => {
            const id = ref?.reference?.split('/')?.[1];
            const serviceRequest = id ? serviceRequests.get(id) : undefined;

            return serviceRequest ? [ServiceRequestWrapper(serviceRequest)] : [];
          }) ?? [];

        [patient, relatedPerson, ...practitioner.values(), ...practitionerRole.values()]
          .filter(Boolean)
          .forEach((resource) => {
            recorderResources.set(`${resource.resourceType}/${resource.id}`, resource);
          });
        const episodeDiagnosis =
          episode?.diagnosis?.flatMap((ref: any) => {
            const conditionId = ref?.condition?.reference?.split('/')?.[1];
            const conditionRank = ref?.condition?.rank;
            const condition = conditionId ? diagnosis.get(conditionId) : undefined;

            if (!condition) return [];

            const recorderReference = condition?.recorder?.reference;
            const recorderResource = recorderReference
              ? recorderResources.get(recorderReference)
              : undefined;

            return [
              {
                ...ConditionWrapper(condition),
                rank: conditionRank,
                recorderResource: recorderResource
                  ? {
                      id: recorderResource.id,
                      display: getRecorderDisplayName(recorderResource),
                    }
                  : undefined,
              },
            ];
          }) ?? [];

        const practitionerWrapper = practitionerR ? PractitionerWrapper(practitionerR) : null;
        const practitionerRoleWrapper = practitionerRoleR
          ? PractitionerRoleWrapper(practitionerRoleR)
          : null;
        const episodeOfCareWrapper = EpisodeOfCareWrapper(episode);
        const patientWrapper = patient ? PatientWrapper(patient) : null;
        const organizationWrapper = organization ? OrganizationWrapper(organization) : null;

        return {
          ...episodeOfCareWrapper,
          includedResources: {
            careManager: practitionerWrapper
              ? {
                  id: practitionerWrapper?.id,
                  display: practitionerWrapper?.getFullName?.() ?? '',
                  role: practitionerRoleWrapper?.getRoleName() ?? '',
                }
              : undefined,
            subject: patientWrapper,
            serviceRequests: episodeServiceRequests,
            conditions: episodeDiagnosis,
            organization: organizationWrapper,
          },
        };
      });
  } catch (error) {
    console.log('eoc error', error);
    return [];
  }
};
